import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import api, { getToken } from "../api/client";
import { endpoints } from "../api/endpoints";
import { useAuth } from "./AuthContext";
import { playNotifySound } from "../utils/notifySound";
import { ensurePushSubscription } from "../utils/pushNotifications";
import { openNotificationUrl } from "../utils/openNotificationUrl";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Unread chat messages received while the user is NOT on /chatboard.
  // Drives the badge on the topbar chat icon; ChatBoard clears it on mount.
  const [chatUnread, setChatUnread] = useState(0);
  const socketRef = useRef(null);

  // A notification is unread if current user's id isn't in readBy (and it isn't marked read locally)
  const myId = user?.employeeid;
  const isRead = useCallback((n) => {
    if (n.read) return true;
    if (Array.isArray(n.readBy) && myId && n.readBy.includes(myId)) return true;
    return false;
  }, [myId]);

  const unreadCount = useMemo(() => items.filter((n) => !isRead(n)).length, [items, isRead]);

  const fetchAll = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await api.get(endpoints.notifications.fetch);
      const data = res.data?.notifications || (Array.isArray(res.data?.data) ? res.data.data : []);
      // newest first
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setItems(data);
    } catch (_) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n._id === id ? { ...n, read: true, readBy: [...(n.readBy || []), myId] } : n)));
    try { await api.post("/api/notification/read", { notificationId: id }); } catch (_) {}
  }, [myId]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.post("/api/notification/read", { all: true }); } catch (_) {}
  }, []);

  const dismiss = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => (n._id || n.id) !== id));
    try {
      await api.post(endpoints.notifications.dismiss, { notificationId: id });
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAll();
    // If the user already granted push permission on this device, quietly
    // refresh the subscription so system notifications keep flowing.
    ensurePushSubscription();
  }, [isAuthenticated, fetchAll]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      return;
    }
    const token = getToken();
    const socket = io({
      auth: { token },
      query: { token },
      // Polling-first with websocket upgrade (socket.io default). websocket-first
      // never falls back when the upgrade is blocked by a proxy, which silently
      // disabled live notifications in those environments.
      transports: ["polling", "websocket"],
      reconnection: true,
    });
    socketRef.current = socket;

    // Join user-specific room so targeted notifications land only here
    if (user?.employeeid) {
      socket.emit("join_user", user.employeeid);
      socket.on("connect", () => socket.emit("join_user", user.employeeid));
    }

    const onNotif = (payload) => {
      setItems((prev) => [{
        _id: payload._id || payload.id || `tmp-${Date.now()}`,
        ...payload,
        read: false,
        createdAt: payload.createdAt || new Date().toISOString(),
      }, ...prev]);
      // silent entries only land in the bell (chat_notify already made the
      // noise for them — mention/channel-add events arrive on both channels).
      if (payload.silent || String(payload.kind || "").startsWith("chat.")) return;
      if (payload.title || payload.message) {
        const severity = payload.severity;
        const toastFn = severity === "danger" ? toast.error : severity === "warning" ? toast.warn : severity === "success" ? toast.success : toast.info;
        toastFn(payload.title ? `${payload.title}: ${payload.message || ""}` : payload.message, {
          onClick: (payload.url || payload.metadata?.meetingLink)
            ? () => openNotificationUrl(payload.url || payload.metadata.meetingLink)
            : undefined,
        });
        playNotifySound("message");
      }
      if (String(payload.kind || "").startsWith("leave.")) {
        window.dispatchEvent(new CustomEvent("hrms:leave-changed", { detail: payload }));
      }
      if (String(payload.kind || "").startsWith("overtime.")) {
        window.dispatchEvent(new CustomEvent("hrms:overtime-changed", { detail: payload }));
      }
    };

    socket.on("notification", onNotif);

    // Chat message notifications (DMs, channel messages, webhook posts).
    // Suppressed while the user is already on the chat page — ChatBoard's own
    // socket renders the message live there and a toast would be noise.
    const onChatNotify = (p) => {
      const onChatPage = window.location.pathname.toLowerCase().startsWith("/chatboard");
      if (p.kind === "mention") {
        // @mentions are louder: they fire even on the chat page, unless the
        // mentioning channel itself is on screen (ChatBoard maintains
        // window.__openChatId for exactly this check).
        if (onChatPage && String(window.__openChatId || "") === String(p.groupId || "")) return;
        if (!onChatPage) setChatUnread((c) => c + 1);
        toast.warn(`@ ${p.from || "Someone"} mentioned you in #${p.groupName || "channel"}: ${p.preview || ""}`, {
          onClick: () => { window.location.href = "/chatboard"; },
        });
        playNotifySound("mention");
        return;
      }
      if (onChatPage) return;
      setChatUnread((c) => c + 1);
      const label = p.kind === "group" ? `#${p.groupName || "channel"} · ${p.from || "Someone"}` : (p.from || "New message");
      toast.info(`💬 ${label}: ${p.preview || ""}`, {
        onClick: () => { window.location.href = "/chatboard"; },
      });
      playNotifySound("message");
    };
    socket.on("chat_notify", onChatNotify);

    socket.on("announcement_new", (p) => onNotif({
      kind: "announcement.posted", title: "📢 New announcement", message: p.title,
      url: "/announcements", severity: "info",
    }));

    // Server forced logout (session revoked from elsewhere)
    const onRevoke = () => {
      toast.error("Your session was revoked. Please log in again.");
      try {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("role");
        sessionStorage.removeItem("employeeid");
        sessionStorage.removeItem("name");
      } catch (_) {}
      window.location.href = "/login";
    };
    socket.on("session_revoked", onRevoke);

    // Module toggles — re-dispatch as a window event so ModulesContext (which
    // sits ABOVE this provider in the tree) can refetch without a reload.
    const onModulesChanged = (p) => {
      window.dispatchEvent(new CustomEvent("hrms:modules_changed", { detail: p }));
    };
    socket.on("modules_changed", onModulesChanged);

    return () => {
      socket.off("notification", onNotif);
      socket.off("chat_notify", onChatNotify);
      socket.off("announcement_new");
      socket.off("session_revoked", onRevoke);
      socket.off("modules_changed", onModulesChanged);
      socket.disconnect();
    };
  }, [isAuthenticated, user?.employeeid]);

  const clearChatUnread = useCallback(() => setChatUnread(0), []);

  const value = useMemo(
    () => ({ items, unreadCount, loading, fetchAll, markAllRead, markRead, dismiss, isRead, chatUnread, clearChatUnread }),
    [items, unreadCount, loading, fetchAll, markAllRead, markRead, dismiss, isRead, chatUnread, clearChatUnread]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationProvider>");
  return ctx;
}
